const isInstallationTypeService = (typeService) =>
  String(typeService || "").toLowerCase().includes("installation");

const resolveInstallationRatesOwnerId = (
  item,
  employees,
  allPersons,
  personManagerStatus
) => {
  const managerEmail = Array.isArray(item?.AssignedManagerEmail)
    ? item.AssignedManagerEmail[0]
    : item?.AssignedManagerEmail;

  if (managerEmail) {
    const managerEmp = employees.find(
      (emp) =>
        emp.EmpEmail &&
        emp.EmpEmail.toLowerCase() === String(managerEmail).trim().toLowerCase()
    );
    if (managerEmp?._id) return managerEmp._id;
  }

  for (const [name, personData] of allPersons) {
    if (!personManagerStatus[name]) continue;

    const managerEmp =
      employees.find(
        (emp) =>
          emp.EmpEmail &&
          personData.email &&
          emp.EmpEmail.toLowerCase() === personData.email.toLowerCase()
      ) ||
      employees.find((emp) => {
        const fullName = `${emp.EmpFirstName || ""} ${emp.EmpLastName || ""}`
          .trim()
          .toLowerCase();
        return fullName === name.trim().toLowerCase();
      });

    if (managerEmp?._id) return managerEmp._id;
    if (personData.personId) return personData.personId;
  }

  const managerQuote = (item?.quotationDetails || []).find((q) => {
    const designation = String(q?.designation || "").toLowerCase();
    return (
      designation.includes("installation") &&
      (designation.includes("manager") ||
        designation.includes("project manager")) &&
      !designation.includes("employee")
    );
  });

  return managerQuote?.createdBy || null;
};

const getTotalDescriptionValue = (
  tasks,
  projectID,
  personID,
  typeService,
  installRateOverrides,
  perdiem,
  averagecost
) => {
  const descriptions = (tasks || []).filter(
    (desc) => desc.projectid === projectID && desc.taskownerid === personID
  );

  let totalValue = descriptions.reduce(
    (sum, desc) => sum + (Number(desc.expenses) || 0),
    0
  );

  if (typeService && typeService.toLowerCase().includes("installation")) {
    let maxTripDuration = 0;
    descriptions.forEach((desc) => {
      if (desc.tripduration) {
        maxTripDuration = Math.max(maxTripDuration, desc.tripduration);
      }
    });

    // Installation manager rows often omit tripduration; fall back to
    // max trip on the same project so per-diem / average cost are added.
    if (maxTripDuration <= 0 && projectID && tasks?.length) {
      const pid = String(projectID || "").trim();
      (tasks || []).forEach((desc) => {
        if (String(desc?.projectid || "").trim() !== pid) return;
        const dst = String(desc?.typeservice || "")
          .trim()
          .toLowerCase();
        if (dst && !dst.includes("installation")) return;
        maxTripDuration = Math.max(
          maxTripDuration,
          Number(desc?.tripduration) || 0
        );
      });
    }

    if (maxTripDuration > 0) {
      const installPerdiem = installRateOverrides?.perdiem ?? perdiem;
      const installAvgcost = installRateOverrides?.avgcost ?? averagecost;
      totalValue +=
        maxTripDuration * installPerdiem + maxTripDuration * installAvgcost;
    }
  }

  return totalValue;
};

const getTotalHardwareCostValue = (items, projectID) => {
  const pid = String(projectID || "").trim();
  if (!pid) return 0;

  const hardwareCost = (items || []).filter(
    (desc) => String(desc?.projectid || "").trim() === pid
  );
  const hardwareByDescription = {};

  hardwareCost.forEach((hw) => {
    const description = hw.assemblepartdescription;
    if (!hardwareByDescription[description]) {
      hardwareByDescription[description] = hw;
    } else if (
      Number(hw.totalprice) >
      Number(hardwareByDescription[description].totalprice)
    ) {
      hardwareByDescription[description] = hw;
    }
  });

  return Object.values(hardwareByDescription).reduce(
    (sum, hw) => sum + (Number(hw.totalprice) || 0),
    0
  );
};

/** Same row-level expense logic as SalesData transformFilteredQuotation. */
export const transformFilteredQuotationToExpenseRows = (
  filteredQuotation,
  {
    employees = [],
    tasks = [],
    items = [],
    useProjectAssignedRates = false,
    hourlyRateQuotations = [],
    perdiem = 0,
    averagecost = 0,
  } = {}
) => {
  const result = [];

  Object.entries(filteredQuotation || {}).forEach(([, typeServices]) => {
    Object.entries(typeServices).forEach(([, serviceItems]) => {
      serviceItems.forEach((item) => {
        const allPersonsMap = new Map();
        const shouldProcess =
          item?.WorkEntries?.length || item?.Assignedperson?.length;
        if (!shouldProcess) return;

        const getEmployeeByEmail = (email) =>
          employees.find(
            (emp) =>
              emp.EmpEmail &&
              emp.EmpEmail.toLowerCase() === email?.toLowerCase()
          );

        const getEmployeeByName = (name) =>
          employees.find((emp) => {
            const fullName = `${emp.EmpFirstName || ""} ${emp.EmpLastName || ""}`
              .trim()
              .toLowerCase();
            return fullName === name.trim().toLowerCase();
          });

        if (item?.WorkEntries?.length) {
          item.WorkEntries.forEach((entry) => {
            const personName = entry?.createdBy?.name;
            const personRoles = entry?.createdBy?.role || [];
            const personId = entry?.taskownerid;

            if (personName && personId) {
              if (!allPersonsMap.has(personName)) {
                allPersonsMap.set(personName, {
                  personId,
                  totalHours: entry.hoursspent || 0,
                  roles: personRoles,
                  email: entry?.createdBy?.email,
                });
              } else {
                allPersonsMap.get(personName).totalHours += entry.hoursspent || 0;
              }
            }
          });
        }

        if (item?.Assignedperson?.length) {
          item.Assignedperson.forEach((assignedPerson) => {
            const personName = assignedPerson.name;
            const personEmail = assignedPerson.email;
            if (!personName || !personEmail) return;

            const currentEmployee =
              getEmployeeByEmail(personEmail) || getEmployeeByName(personName);
            if (!currentEmployee || allPersonsMap.has(personName)) return;

            allPersonsMap.set(personName, {
              personId: currentEmployee._id || currentEmployee.id,
              totalHours: 0,
              roles: currentEmployee?.customRoles || [],
              email: personEmail,
            });
          });
        }

        const allPersons = Array.from(allPersonsMap.entries());
        if (allPersons.length === 0) return;

        const personManagerStatus = {};
        allPersons.forEach(([name, personData]) => {
          personManagerStatus[name] = personData.roles.some((role) =>
            role.toLowerCase().includes("manager")
          );
        });

        const sortedPersons = allPersons.sort(([nameA], [nameB]) => {
          const isManagerA = personManagerStatus[nameA];
          const isManagerB = personManagerStatus[nameB];
          if (isManagerA && !isManagerB) return -1;
          if (!isManagerA && isManagerB) return 1;
          return 0;
        });

        const isHardwareService = item?.TypeService?.toLowerCase().includes(
          "hardware"
        );
        let hasProjectManager = false;
        if (isHardwareService) {
          hasProjectManager = sortedPersons.some(
            ([name]) => personManagerStatus[name]
          );
        }

        let hardwareCostAssigned = false;
        const usedQuotationIds = new Set();
        const isInstallationService = isInstallationTypeService(item?.TypeService);
        const installationRatesOwnerId = isInstallationService
          ? resolveInstallationRatesOwnerId(
              item,
              employees,
              allPersons,
              personManagerStatus
            )
          : null;
        const matchesInstallationRatesScope = (q) => {
          if (!isInstallationService || !installationRatesOwnerId) return true;
          return (
            String(q?.createdBy || "") === String(installationRatesOwnerId)
          );
        };

        sortedPersons.forEach(([name, personData]) => {
          const totalHours = personData.totalHours;
          const personId = personData.personId;
          const roles = personData.roles;

          const currentEmployee =
            getEmployeeByEmail(personData.email) || getEmployeeByName(name);
          const finalPersonId = currentEmployee?._id || personId;

          const getQuotationRecordId = (q) =>
            q._id || `${q.personid ?? "node"}_${q.designation ?? ""}`;

          let matchedQuotation = null;

          if (
            !useProjectAssignedRates &&
            hourlyRateQuotations?.length > 0 &&
            finalPersonId
          ) {
            const serviceTypeMatch = item?.TypeService?.toLowerCase() || "";
            matchedQuotation = hourlyRateQuotations.find((q) => {
              const qDesignation = q.designation?.toLowerCase() || "";
              return (
                q.personid === finalPersonId &&
                qDesignation.includes(serviceTypeMatch) &&
                matchesInstallationRatesScope(q) &&
                !usedQuotationIds.has(getQuotationRecordId(q))
              );
            });
          }

          if (!matchedQuotation) {
            matchedQuotation = item?.quotationDetails?.find(
              (q) =>
                (String(q.personid) === String(finalPersonId) ||
                  q.personid === finalPersonId) &&
                (!useProjectAssignedRates ||
                  matchesInstallationRatesScope(q)) &&
                !usedQuotationIds.has(getQuotationRecordId(q))
            );
          }

          let finalMatchedQuotation = matchedQuotation;

          if (
            !finalMatchedQuotation &&
            (!useProjectAssignedRates || !isHardwareService)
          ) {
            const isManager = roles.some((role) =>
              role.toLowerCase().includes("manager")
            );
            const isOperations = roles.some((role) =>
              role.toLowerCase().includes("operations") ||
              role.toLowerCase().includes("employee")
            );

            if (isManager) {
              const managerQuotes = item?.quotationDetails?.filter((q) => {
                const qDesignation = q.designation?.toLowerCase() || "";
                return (
                  !usedQuotationIds.has(getQuotationRecordId(q)) &&
                  matchesInstallationRatesScope(q) &&
                  (qDesignation.includes("project manager") ||
                    (qDesignation.includes("manager") &&
                      !qDesignation.includes("operations"))) &&
                  !qDesignation.includes("employee")
                );
              });
              finalMatchedQuotation =
                managerQuotes?.find((q) =>
                  q.designation?.toLowerCase().includes("project manager")
                ) || managerQuotes?.[0];
            } else if (isOperations) {
              let employeeQuotes = [];
              if (
                !useProjectAssignedRates &&
                hourlyRateQuotations?.length > 0
              ) {
                const serviceTypeMatch = item?.TypeService?.toLowerCase() || "";
                employeeQuotes = hourlyRateQuotations.filter((q) => {
                  const qDesignation = q.designation?.toLowerCase() || "";
                  return (
                    !usedQuotationIds.has(getQuotationRecordId(q)) &&
                    matchesInstallationRatesScope(q) &&
                    qDesignation.includes(serviceTypeMatch) &&
                    !qDesignation.includes("manager") &&
                    !qDesignation.includes("project manager") &&
                    (qDesignation.includes("operations") ||
                      qDesignation.includes("employee"))
                  );
                });
              }
              if (employeeQuotes.length === 0) {
                employeeQuotes =
                  item?.quotationDetails?.filter((q) => {
                    const qDesignation = q.designation?.toLowerCase() || "";
                    return (
                      !usedQuotationIds.has(getQuotationRecordId(q)) &&
                      matchesInstallationRatesScope(q) &&
                      !qDesignation.includes("manager") &&
                      !qDesignation.includes("project manager") &&
                      (qDesignation.includes("operations") ||
                        qDesignation.includes("employee"))
                    );
                  }) || [];
              }

              const quoteByPersonId = employeeQuotes?.find(
                (q) =>
                  q.personid &&
                  q.personid === finalPersonId &&
                  !usedQuotationIds.has(getQuotationRecordId(q))
              );
              if (quoteByPersonId) {
                finalMatchedQuotation = quoteByPersonId;
              } else if (employeeQuotes?.length > 0) {
                finalMatchedQuotation = employeeQuotes.find(
                  (q) => !usedQuotationIds.has(getQuotationRecordId(q))
                );
              }
            } else {
              finalMatchedQuotation = item?.quotationDetails?.find(
                (q) =>
                  !usedQuotationIds.has(getQuotationRecordId(q)) &&
                  matchesInstallationRatesScope(q) &&
                  roles.some((role) =>
                    q.designation?.toLowerCase().includes(role?.toLowerCase())
                  )
              );
            }
          }

          if (finalMatchedQuotation) {
            usedQuotationIds.add(getQuotationRecordId(finalMatchedQuotation));
          }

          const capitalizedRoles = roles.map(
            (role) =>
              role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
          );
          const hourlyRate =
            (finalMatchedQuotation?.hourlysalaray || 0) +
            (finalMatchedQuotation?.overheadpercentage || 0);
          const totalCost = totalHours * hourlyRate;
          const descriptionTotal = getTotalDescriptionValue(
            tasks,
            item.ProjectID,
            finalPersonId,
            item.TypeService,
            useProjectAssignedRates
              ? {
                  perdiem: item.projectPerdiem,
                  avgcost: item.projectAvgcost,
                }
              : undefined,
            perdiem,
            averagecost
          );
          const hardwareTotal = getTotalHardwareCostValue(items, item.ProjectID);
          const isOperationsRole = capitalizedRoles.includes("Operations");
          const isProjectManagerRole = personManagerStatus[name];

          let shouldGetHardwareCost = false;
          if (isHardwareService && !hasProjectManager) {
            if (isOperationsRole && !hardwareCostAssigned) {
              shouldGetHardwareCost = true;
              hardwareCostAssigned = true;
            }
          }

          let grandTotal = 0;
          if (isHardwareService) {
            if (isOperationsRole) {
              grandTotal = shouldGetHardwareCost
                ? hardwareTotal + descriptionTotal
                : descriptionTotal;
            } else if (isProjectManagerRole) {
              grandTotal = hardwareTotal + descriptionTotal;
            } else {
              grandTotal = descriptionTotal + totalCost;
            }
          } else {
            grandTotal = descriptionTotal + totalCost;
          }

          result.push({ grandTotal, typeService: item.TypeService });
        });
      });
    });
  });

  return result;
};

export const sumActualExpenseGrandTotal = (filteredQuotation, options) =>
  transformFilteredQuotationToExpenseRows(filteredQuotation, options).reduce(
    (sum, row) => sum + (Number(row.grandTotal) || 0),
    0
  );
